import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form as FormikForm, Field } from 'formik';
import * as Yup from 'yup';
import cx from 'classnames';

import tgai from '../TourGuideAi.module.scss';

const buildValidationSchema = (fields) => {
  const shape = {};
  const groups = {};

  fields.forEach((field) => {
    if (field.requiredGroup) {
      if (!groups[field.requiredGroup]) groups[field.requiredGroup] = [];
      groups[field.requiredGroup].push(field);
    }
  });

  fields.forEach((field) => {
    let validator = Yup.string();
    if (field.required) {
      validator = validator.required(`${field.label} is required`);
    }
    if (field.requiredGroup) {
      const siblings = groups[field.requiredGroup]
        .filter((f) => f.name !== field.name)
        .map((f) => f.name);
      validator = validator.test(
        'require-one-of-group',
        'Please provide either an email address or phone number',
        function contactTest(value) {
          return !!value || siblings.some((name) => !!this.parent[name]);
        },
      );
    }
    if (field.type === 'email') {
      validator = validator.email('Please enter a valid email address');
    }
    shape[field.name] = validator;
  });
  return Yup.object().shape(shape);
};

const buildInitialValues = (fields, savedLeadInfo) => {
  const values = {};
  fields.forEach((field) => {
    values[field.name] = (savedLeadInfo && savedLeadInfo[field.name]) || '';
  });
  return values;
};

const Form = ({
  formFields,
  submitButton,
  clearDataLink,
  formState: { isFormSubmitted, savedLeadInfo },
  handlers: { onFormSubmit, onButtonClick },
}) => {
  const validationSchema = useMemo(() => buildValidationSchema(formFields), [formFields]);
  const initialValues = useMemo(
    () => buildInitialValues(formFields, savedLeadInfo),
    [formFields, savedLeadInfo],
  );

  const showClearLink = !isFormSubmitted && clearDataLink && savedLeadInfo && (
    savedLeadInfo.firstName || savedLeadInfo.lastName || savedLeadInfo.email
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => onFormSubmit(submitButton, values)}
    >
      {({ errors, touched }) => (
        <fieldset disabled={isFormSubmitted}>
          <FormikForm>
            {formFields.map((field) => (
              <React.Fragment key={field.name}>
                <Field
                  type={field.type}
                  name={field.name}
                  placeholder={field.placeholder || field.label}
                  className={errors[field.name] && touched[field.name] ? tgai.error : undefined}
                />
                {errors[field.name] && touched[field.name] && (
                <div className={tgai.errorMessage}>{errors[field.name]}</div>
                )}
              </React.Fragment>
            ))}
            <button
              type="submit"
              className={cx(tgai.btn, tgai.primary)}
            >
              {submitButton.text}
            </button>
            {showClearLink && (
              <button
                type="button"
                onClick={() => onButtonClick(clearDataLink)}
                className={cx(tgai.textBtn)}
              >
                {clearDataLink.text}
              </button>
            )}
          </FormikForm>
        </fieldset>
      )}
    </Formik>
  );
};

Form.propTypes = {
  formFields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
  })).isRequired,
  submitButton: PropTypes.shape({
    text: PropTypes.string.isRequired,
  }).isRequired,
  clearDataLink: PropTypes.shape({
    text: PropTypes.string.isRequired,
  }),
  formState: PropTypes.shape({
    isFormSubmitted: PropTypes.bool.isRequired,
    savedLeadInfo: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
    }),
  }).isRequired,
  handlers: PropTypes.shape({
    onFormSubmit: PropTypes.func.isRequired,
    onButtonClick: PropTypes.func.isRequired,
  }).isRequired,
};

Form.defaultProps = {
  clearDataLink: null,
};

export default Form;
